import React, { useState } from "react";
import { HeaderProps } from "./types";
import Icon from "@/ui/Icon";
import { DEFAULT_LINKS, PROTECTED_LINKS } from "@/lib/constants";
import useAuthentication from "@/hooks/useAuthentication";
import usePathWithSlug from "@/hooks/usePathWithSlug";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/library/accordion";
import HeaderLinks from "./HeaderLinks";
import HeaderUserArea from "./HeaderUserArea";

export const Header: React.FC<HeaderProps> = ({ links = [] }) => {
  const pathWithSlug = usePathWithSlug();
  const { user } = useAuthentication();
  const [menuOpen, setMenuOpen] = useState(false);
  const defaultLinks = user ? [...DEFAULT_LINKS, ...PROTECTED_LINKS] : DEFAULT_LINKS;
  const linksToShow = links.length === 0 ? defaultLinks : links;

  return (
    <header className="relative z-50 px-6 py-4 text-white xl:px-10">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 lg:flex-row lg:items-center lg:gap-0">
        <div className="flex w-full items-center justify-between text-white md:w-auto">
          <a href={pathWithSlug("/")} aria-label="Go to homepage">
            <Icon name="logo" className="mr-14 w-20" />
          </a>
          <button
            className="inline-flex items-center justify-center rounded-md p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 lg:hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-controls="primary-navigation"
            aria-expanded={menuOpen}
          >
            <Icon name="menu" />
          </button>
        </div>

        {/* Mobile accordion */}
        <Accordion
          type="single"
          collapsible
          value={menuOpen ? "menu" : undefined}
          onValueChange={(v) => setMenuOpen(Boolean(v))}
          className="w-full lg:hidden"
        >
          <AccordionItem value="menu">
            <AccordionContent>
              <div className="flex w-full flex-col items-center justify-between gap-4 py-2">
                <div className="mr-auto flex w-full flex-col items-center gap-3">
                  {!!linksToShow.length && (
                    <HeaderLinks links={linksToShow} className="w-full" onItemClick={() => setMenuOpen(false)} />
                  )}
                </div>
                <HeaderUserArea />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Desktop navigation */}
        <div className="hidden w-full items-center justify-between lg:visible lg:flex lg:flex-row">
          <div className="mr-auto flex flex-1 flex-col items-center gap-4 lg:flex-row lg:gap-6">
            {!!linksToShow.length && <HeaderLinks links={linksToShow} className="gap-4" />}
          </div>
          <HeaderUserArea className="flex flex-col items-center gap-3 text-white lg:flex-row lg:items-start" />
        </div>
      </div>
    </header>
  );
};
